// Define interfaces for the data structure
import { OVERDUE } from "~/assets/constants/orderStates"

interface OrderItem {
	id: number
	quantity: number
	title: string
	sku: string
	description: string | null
	userId: number
	orderId: number
	transactionId: number | null
	eventCredentialTypeId: number
	canceled: number
	paid: number
	shipped: number
	refunded: number
	amtSubtotal: string
	amtPriceAdjustment: string
	amtDiscount: string
	amtGst: string
	amtPst: string
	amtFees: string
	amtShipping: string
	amtTotal: string
	data: any | null
	deletedAt: string | null
	createdAt: string
	updatedAt: string
	reservationSelectedSeatsArray: any[]
	images: string[]
}

interface Address {
	firstname: string
	lastname: string
	address: string | null
	address2: string | null
	country: string
	state: string
	city: string | null
	zip: string | null
}

interface OrderPayment {
	id: number
	lastDigits: string
	cardCategory: string
	cardType: string
	paymentProcesser: string
	paymentStatusId: string
	status: string
	installmentNumber: number
	value: string
	statusCheckAttempts: number
	scheduledTo: string
	originalScheduledTo: string
	patronExtensionUsed: boolean
	createdAt: string
	updatedAt: string
	refundResponse: string
	refundExcluded: string
	paymentFailedResponseCount: number
	lastPaymentErrorMessage: string
}

interface Order {
	id: number
	//Possible States: CREATED, PAID, REFUNDED, CANCELED, PENDING, OVERDUE
	status: string
	shipped: boolean
	paymentStatus: string
	currency: string
	amtSubtotal: string
	amtPriceAdjustments: string
	amtTotal: string
	amtTaxPst: string
	amtTaxGst: string
	amtTax: string
	amtRemaining: number
	amtPaid: number
	amtRefunded: string
	amtDiscount: string
	discountCode: string
	insured: boolean
	amtInsurance: number
	amtInsured: number
	createdAt: string
	items?: OrderItem[]
	payments: OrderPayment[]
	billingAddress: Address
	shippingAddress: Address
	eventId: number
}

interface OrdersState {
	orders: Order[]
	orderItems: Record<number, OrderItem[]>
}

function parseDate(utcDateString: string) {
	const date = new Date(utcDateString)
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, "0")
	const day = String(date.getDate()).padStart(2, "0")
	// Format as YYYY-MM-DD
	return `${year}-${month}-${day}`
}

function convertJsonToOrder(json: any): Order {
	let order: Order = {
		id: json.id,
		status: json.status,
		shipped: json.shipped,
		paymentStatus: json.payment_status,
		currency: json.currency,
		amtSubtotal: json.amt_subtotal,
		amtPriceAdjustments: json.amt_price_adjustments,
		amtTotal: json.amt_total,
		amtTaxPst: json.amt_tax_pst,
		amtTaxGst: json.amt_tax_gst,
		amtTax: json.amt_tax,
		amtRemaining: json.amt_remaining,
		amtPaid: json.amt_paid,
		amtRefunded: json.amt_refunded,
		amtDiscount: json.amt_discount,
		discountCode: json.discount_code,
		insured: json.insured,
		amtInsurance: json.amt_insurance,
		amtInsured: json.amt_insured,
		createdAt: parseDate(json.created_at),
		payments: json.payments.map(
			(payment: any): OrderPayment => ({
				id: payment.id,
				lastDigits: payment.last_digits,
				cardCategory: payment.card_category,
				cardType: payment.card_type,
				paymentProcesser: payment.payment_processer,
				paymentStatusId: payment.payment_status_id,
				status: payment.status ? payment.status.toString().toLowerCase() : payment.status,
				installmentNumber: payment.installment_number,
				value: payment.value,
				statusCheckAttempts: payment.status_check_attempts,
				scheduledTo: payment.scheduled_to,
				originalScheduledTo: payment.original_scheduled_to,
				patronExtensionUsed: payment.patron_extension_used,
				createdAt: payment.created_at,
				updatedAt: payment.updated_at,
				refundResponse: payment.refund_response,
				refundExcluded: payment.refund_excluded,
				paymentFailedResponseCount: payment.payment_failed_response_count,
				lastPaymentErrorMessage: payment.last_payment_error_message,
			}),
		),
		billingAddress: {
			firstname: json.billing_address.firstname,
			lastname: json.billing_address.lastname,
			address: json.billing_address.address,
			address2: json.billing_address.address2,
			country: json.billing_address.country,
			state: json.billing_address.state,
			city: json.billing_address.city,
			zip: json.billing_address.zip,
		},
		shippingAddress: {
			firstname: json.shipping_address.firstname,
			lastname: json.shipping_address.lastname,
			address: json.shipping_address.address,
			address2: json.shipping_address.address2,
			country: json.shipping_address.country,
			state: json.shipping_address.state,
			city: json.shipping_address.city,
			zip: json.shipping_address.zip,
		},
		eventId: json.event_id ?? 0, //An order without an event means there were no orderItems associated to it
	}

	if (json.items) {
		order.items = json.items.map(
			(item: any): OrderItem => ({
				id: item.id,
				quantity: item.quantity,
				title: item.title,
				sku: item.sku,
				description: item.description,
				userId: item.user_id,
				orderId: item.order_id,
				transactionId: item.transaction_id,
				eventCredentialTypeId: item.event_credential_type_id,
				canceled: item.canceled,
				paid: item.paid,
				shipped: item.shipped,
				refunded: item.refunded,
				amtSubtotal: item.amt_subtotal,
				amtPriceAdjustment: item.amt_price_adjustment,
				amtDiscount: item.amt_discount,
				amtGst: item.amt_gst,
				amtPst: item.amt_pst,
				amtFees: item.amt_fees,
				amtShipping: item.amt_shipping,
				amtTotal: item.amt_total,
				data: item.data,
				deletedAt: item.deleted_at,
				createdAt: item.created_at,
				updatedAt: item.updated_at,
				reservationSelectedSeatsArray: item.reservationSelectedSeatsArray,
				images:
					item.event_credential_type.image && item.event_credential_type.image.indexOf("http") >= 0
						? JSON.parse(item.event_credential_type.image)
						: [],
			}),
		)
	}

	order.payments.sort((a: OrderPayment, b: OrderPayment) => {
		if (a.scheduledTo < b.scheduledTo) return -1
		if (a.scheduledTo > b.scheduledTo) return 1
		return 0
	})
	return order
}

// Define the Pinia store
export const useOrderStore = defineStore("orderStore", {
	state: (): OrdersState => ({
		orders: [],
		//Stored the orderItems in a separate map since the userSummary does not return orderItems
		orderItems: {},
	}),

	getters: {
		getOrders: (state: OrdersState) => () => {
			return state.orders
		},
		getOrderById: (state: OrdersState) => (id: number) => {
			return state.orders.find((order: Order) => order.id === id)
		},
		getOrderItemsById: (state: OrdersState) => (id: number) => {
			return state.orderItems[id] || []
		},
		getTotalOrders: (state: OrdersState) => {
			return state.orders.length
		},
		getFirstOverdueOrder: (state: OrdersState) => {
			return state.orders.find((order: Order) => order.status.toUpperCase() === OVERDUE)
		},
		getPendingPaymentsForOrder: (state: OrdersState) => (order: Order) => {
			return order.payments.filter((payment) => +payment.paymentStatusId === 1 || +payment.paymentStatusId === 11)
		},
		getOldestOverduePaymentForOrder: (state: OrdersState) => (order: Order) => {
			// Find payments from the order that are overdue and get the oldest one
			const overduePayments = order.payments.filter(
				(payment) => +payment.paymentStatusId === 1 || +payment.paymentStatusId === 11,
			)
			if (!overduePayments.length) return null
			return overduePayments[0]
		},
	},

	actions: {
		storeOrdersFromJson(orders: any) {
			orders.forEach((order: any) => {
				const convertedOrder = convertJsonToOrder(order)
				this.orders.push(convertedOrder)
				if (convertedOrder.items) {
					this.orderItems[convertedOrder.id] = convertedOrder.items
				}
			})
		},
		updateOrdersFromJson(orders: any) {
			orders.forEach((updatedOrder: any) => {
				const index = this.orders.findIndex((order: Order) => order.id === updatedOrder.id)
				const convertedOrder = convertJsonToOrder(updatedOrder)
				if (index !== -1) {
					this.orders[index] = convertedOrder
				} else {
					this.orders.push(convertedOrder)
				}
				// User Summary API call should not modify order items
			})
		},
		updateOrder(updatedOrder: any) {
			const index = this.orders.findIndex((order: Order) => order.id === updatedOrder.id)
			const convertedOrder = convertJsonToOrder(updatedOrder)
			if (index !== -1) {
				this.orders[index] = convertedOrder
			} else {
				this.orders.push(convertedOrder)
			}

			if (convertedOrder.items) {
				this.orderItems[convertedOrder.id] = convertedOrder.items
			}
		},
		clear() {
			this.orders = []
			this.orderItems = {}
		},
	},

	persist: {
		key: "up_orderStore", // Make sure this key is unique
		storage: localStorage, // or cookies if you're using cookie storage
		serializer: {
			serialize: (state: OrdersState) => {
				const clonedState = { ...state }
				return JSON.stringify(clonedState)
			},
			deserialize: (state: OrdersState) => {
				const parsedState = JSON.parse(state)
				return parsedState
			},
		},
	},
})
